import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import ShowChartIcon from '@material-ui/icons/ShowChart';

import CategoryCountGraph from './CategoryCountGraph';

// Árbol fijo a 2 niveles (categoría → subcategoría, ver CategoryNode.level) — Collapse
// anidado explícito, sin recursión genérica (decisión X-A del doc de shaping).
//
// Cada fila con panel desplegable (supermercado/categoría) tiene DOS estados
// independientes, `childrenOpen` y `chartOpen`, cada uno con su propio <Collapse> —
// abrir el gráfico no cierra los hijos (ni viceversa), para no perder qué
// categorías/subcategorías quedaban desplegadas al mirar un gráfico y volver.

function SubcategoryRow({ subcategory }) {
  const [chartOpen, setChartOpen] = useState(false);

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell padding="checkbox" />
        <TableCell>{subcategory.name}</TableCell>
        <TableCell align="right">{subcategory.count ?? '—'}</TableCell>
        <TableCell align="right">
          <IconButton
            size="small"
            aria-label="Ver historial"
            onClick={() => setChartOpen(!chartOpen)}
          >
            <ShowChartIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0, borderBottom: 0 }} colSpan={5}>
          <Collapse in={chartOpen} timeout="auto" unmountOnExit>
            <Box p={2}>
              <CategoryCountGraph
                groupBy="subcategory"
                nodeId={subcategory.id}
                title={subcategory.name}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

SubcategoryRow.propTypes = {
  subcategory: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    count: PropTypes.number,
  }).isRequired,
};

function CategoryRow({ category }) {
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const hasSubcategories = category.subcategories.length > 0;

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell padding="checkbox">
          {hasSubcategories && (
            <IconButton size="small" aria-label="Desplegar subcategorías" onClick={() => setChildrenOpen((open) => !open)}>
              {childrenOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{category.name}</TableCell>
        <TableCell align="right">{category.count ?? '—'}</TableCell>
        <TableCell align="right">
          <IconButton
            size="small"
            aria-label="Ver historial"
            onClick={() => setChartOpen((open) => !open)}
          >
            <ShowChartIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0, borderBottom: 0 }} colSpan={5}>
          <Collapse in={chartOpen} timeout="auto" unmountOnExit>
            <Box p={2}>
              <CategoryCountGraph
                groupBy="category"
                nodeId={category.id}
                title={category.name}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0, borderBottom: 0 }} colSpan={5}>
          <Collapse in={childrenOpen} timeout="auto" unmountOnExit>
            <Table size="small">
              <TableBody>
                {category.subcategories.map((subcategory) => (
                  <SubcategoryRow key={subcategory.id} subcategory={subcategory} />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

CategoryRow.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    count: PropTypes.number,
    subcategories: PropTypes.array,
  }).isRequired,
};

function SupermarketRow({ supermarket }) {
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const hasCategories = supermarket.categories.length > 0;

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          {hasCategories && (
            <IconButton size="small" aria-label="Desplegar categorías" onClick={() => setChildrenOpen((open) => !open)}>
              {childrenOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell padding="checkbox" />
        <TableCell>
          <Typography variant="subtitle2">{supermarket.name}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="subtitle2">{supermarket.total}</Typography>
        </TableCell>
        <TableCell align="right">
          <IconButton
            size="small"
            aria-label="Ver historial"
            onClick={() => setChartOpen((open) => !open)}
          >
            <ShowChartIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0, borderBottom: 0 }} colSpan={5}>
          <Collapse in={chartOpen} timeout="auto" unmountOnExit>
            <Box p={2}>
              <CategoryCountGraph
                groupBy="supermarket"
                supermarketId={supermarket.id}
                title={supermarket.name}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0, borderBottom: 0 }} colSpan={5}>
          <Collapse in={childrenOpen} timeout="auto" unmountOnExit>
            <Table size="small">
              <TableBody>
                {supermarket.categories.map((category) => (
                  <CategoryRow key={category.id} category={category} />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

SupermarketRow.propTypes = {
  supermarket: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    total: PropTypes.number,
    categories: PropTypes.array,
  }).isRequired,
};

function Results({ supermarkets, loading }) {
  return (
    <Card>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell padding="checkbox" />
              <TableCell>Supermercado / Categoría / Subcategoría</TableCell>
              <TableCell align="right">Cantidad</TableCell>
              <TableCell align="right">Historial</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {supermarkets.map((supermarket) => (
              <SupermarketRow key={supermarket.id} supermarket={supermarket} />
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

Results.propTypes = {
  supermarkets: PropTypes.array,
  loading: PropTypes.bool,
};

Results.defaultProps = {
  supermarkets: [],
  loading: false,
};

export default Results;
